import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { PenLine, Wand2, Copy, Coins, Loader2, FileText, Share2 } from "lucide-react";

export default function AICopy() {
  const { isOwner, refreshBalance } = useAuth();
  const [pricing, setPricing] = useState(null);
  const [address, setAddress] = useState("");
  const [features, setFeatures] = useState("");
  const [style, setStyle] = useState("cinematic");
  const [language, setLanguage] = useState("en");
  const [length, setLength] = useState("medium");
  const [descResult, setDescResult] = useState(null);
  const [socialResult, setSocialResult] = useState(null);
  const [busy, setBusy] = useState(null);

  useEffect(() => { api.get("/ai/pricing").then((r) => setPricing(r.data)); }, []);

  const runDescription = async () => {
    if (!features.trim()) return toast.error("Add features first");
    setBusy("desc");
    try {
      const { data } = await api.post("/ai/description", {
        address, features, style, language, length,
      });
      setDescResult(data);
      toast.success("Description generated");
      refreshBalance();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI failed");
    } finally { setBusy(null); }
  };

  const runSocial = async () => {
    if (!features.trim()) return toast.error("Add features first");
    setBusy("social");
    try {
      const { data } = await api.post("/ai/social", {
        address, features, style, language,
      });
      setSocialResult(data);
      toast.success("Captions generated");
      refreshBalance();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI failed");
    } finally { setBusy(null); }
  };

  const copy = async (text, label = "Copied") => {
    await navigator.clipboard.writeText(text);
    toast.success(label);
  };

  return (
    <div className="p-6 md:p-10 lg:p-14 max-w-[1400px]">
      <div className="mb-10">
        <div className="font-mono-tech text-[10px] text-amber-400 mb-3">AI · COPYWRITING · STUDIO</div>
        <h1 className="font-heading text-4xl sm:text-5xl tracking-tighter font-light leading-none">
          Words that <span className="font-bold text-amber-400">sell.</span>
        </h1>
        <p className="text-zinc-400 mt-4 max-w-2xl">
          Generate listing descriptions and social captions in seconds — powered by GPT-5.2, tuned for luxury real estate.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Input */}
        <div className="lg:col-span-5 space-y-4">
          <div className="bg-[#121214] border border-white/5 rounded-lg p-5 space-y-4">
            <div>
              <Label className="font-mono-tech text-[10px] text-zinc-500">ADDRESS</Label>
              <Input value={address} onChange={(e) => setAddress(e.target.value)}
                placeholder="Cala Vadella, Ibiza, Spain" data-testid="ai-address"
                className="mt-2 bg-[#0b0b0d] border-white/10 h-11" />
            </div>
            <div>
              <Label className="font-mono-tech text-[10px] text-zinc-500">FEATURES · DESCRIPTION *</Label>
              <Textarea value={features} onChange={(e) => setFeatures(e.target.value)}
                data-testid="ai-features"
                placeholder="Modern 4-bed villa, infinity pool, panoramic sea view, private garden, chef's kitchen…"
                rows={7}
                className="mt-2 bg-[#0b0b0d] border-white/10" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="font-mono-tech text-[10px] text-zinc-500">STYLE</Label>
                <Select value={style} onValueChange={setStyle}>
                  <SelectTrigger data-testid="ai-style" className="mt-2 bg-[#0b0b0d] border-white/10">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1a1a1e] border-white/10">
                    <SelectItem value="luxury">Luxury</SelectItem>
                    <SelectItem value="cinematic">Cinematic</SelectItem>
                    <SelectItem value="modern">Modern</SelectItem>
                    <SelectItem value="minimal">Minimal</SelectItem>
                    <SelectItem value="social">Social</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="font-mono-tech text-[10px] text-zinc-500">LANGUAGE</Label>
                <Select value={language} onValueChange={setLanguage}>
                  <SelectTrigger data-testid="ai-lang" className="mt-2 bg-[#0b0b0d] border-white/10">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1a1a1e] border-white/10">
                    <SelectItem value="en">English</SelectItem>
                    <SelectItem value="nl">Nederlands</SelectItem>
                    <SelectItem value="es">Español</SelectItem>
                    <SelectItem value="fr">Français</SelectItem>
                    <SelectItem value="de">Deutsch</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="font-mono-tech text-[10px] text-zinc-500">LENGTH</Label>
                <Select value={length} onValueChange={setLength}>
                  <SelectTrigger data-testid="ai-length" className="mt-2 bg-[#0b0b0d] border-white/10">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1a1a1e] border-white/10">
                    <SelectItem value="short">Short</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="long">Long</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Button onClick={runDescription} disabled={busy === "desc" || !features.trim()}
              data-testid="ai-run-description"
              className="h-11 bg-amber-500 hover:bg-amber-600 text-black font-semibold">
              {busy === "desc" ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <FileText className="w-4 h-4 mr-2" strokeWidth={1.5} />}
              Listing description
              {!isOwner && pricing && (
                <span className="ml-2 text-[10px] font-mono-tech opacity-75 flex items-center gap-1">
                  <Coins className="w-3 h-3" /> {pricing.description_credits}
                </span>
              )}
            </Button>
            <Button onClick={runSocial} disabled={busy === "social" || !features.trim()}
              data-testid="ai-run-social"
              variant="outline"
              className="h-11 bg-transparent border-white/15 hover:bg-white/5">
              {busy === "social" ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Share2 className="w-4 h-4 mr-2" strokeWidth={1.5} />}
              Social captions
              {!isOwner && pricing && (
                <span className="ml-2 text-[10px] font-mono-tech opacity-75 flex items-center gap-1">
                  <Coins className="w-3 h-3" /> {pricing.social_credits}
                </span>
              )}
            </Button>
          </div>
        </div>

        {/* Output */}
        <div className="lg:col-span-7">
          <Tabs defaultValue="description" className="space-y-4">
            <TabsList className="bg-[#121214] border border-white/5 p-1">
              <TabsTrigger value="description" data-testid="tab-desc"
                className="data-[state=active]:bg-amber-500 data-[state=active]:text-black gap-2">
                <FileText className="w-3.5 h-3.5" strokeWidth={1.5} /> Description
              </TabsTrigger>
              <TabsTrigger value="social" data-testid="tab-social"
                className="data-[state=active]:bg-amber-500 data-[state=active]:text-black gap-2">
                <Share2 className="w-3.5 h-3.5" strokeWidth={1.5} /> Social
              </TabsTrigger>
            </TabsList>

            <TabsContent value="description">
              {descResult ? (
                <div className="bg-[#121214] border border-white/5 rounded-lg p-6 space-y-5" data-testid="desc-result">
                  {descResult.title && (
                    <div>
                      <div className="flex items-center justify-between mb-1.5">
                        <Label className="font-mono-tech text-[10px] text-zinc-500">TITLE</Label>
                        <button onClick={() => copy(descResult.title, "Title copied")}
                          className="text-zinc-500 hover:text-amber-400"><Copy className="w-3.5 h-3.5" /></button>
                      </div>
                      <div className="font-heading text-2xl font-bold tracking-tight">{descResult.title}</div>
                    </div>
                  )}
                  {descResult.description && (
                    <div>
                      <div className="flex items-center justify-between mb-1.5">
                        <Label className="font-mono-tech text-[10px] text-zinc-500">BODY</Label>
                        <button onClick={() => copy(descResult.description, "Description copied")}
                          className="text-zinc-500 hover:text-amber-400"><Copy className="w-3.5 h-3.5" /></button>
                      </div>
                      <p className="text-zinc-200 leading-relaxed">{descResult.description}</p>
                    </div>
                  )}
                  {Array.isArray(descResult.highlights) && descResult.highlights.length > 0 && (
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <Label className="font-mono-tech text-[10px] text-zinc-500">HIGHLIGHTS</Label>
                        <button onClick={() => copy(descResult.highlights.map((h) => "• " + h).join("\n"), "Highlights copied")}
                          className="text-zinc-500 hover:text-amber-400"><Copy className="w-3.5 h-3.5" /></button>
                      </div>
                      <ul className="space-y-2 text-sm text-zinc-300">
                        {descResult.highlights.map((h, i) => (
                          <li key={i} className="flex gap-2">
                            <span className="text-amber-400">·</span>
                            <span>{h}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ) : (
                <EmptyResult label="Listing description will appear here." icon={FileText} />
              )}
            </TabsContent>

            <TabsContent value="social">
              {socialResult ? (
                <div className="space-y-3" data-testid="social-result">
                  {[
                    ["instagram", "Instagram", "IG"],
                    ["tiktok", "TikTok", "TT"],
                    ["facebook", "Facebook", "FB"],
                    ["linkedin", "LinkedIn", "LI"],
                    ["youtube_title", "YouTube title", "YT"],
                  ].map(([key, label]) => {
                    const val = socialResult[key];
                    if (!val) return null;
                    return (
                      <div key={key} className="bg-[#121214] border border-white/5 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <Label className="font-mono-tech text-[10px] text-zinc-500">{label.toUpperCase()}</Label>
                          <button onClick={() => copy(val, `${label} copied`)}
                            className="text-zinc-500 hover:text-amber-400"><Copy className="w-3.5 h-3.5" /></button>
                        </div>
                        <p className="text-sm text-zinc-200 whitespace-pre-wrap leading-relaxed">{val}</p>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <EmptyResult label="Instagram, TikTok, Facebook, LinkedIn & YouTube captions will appear here." icon={Share2} />
              )}
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}

function EmptyResult({ label, icon: Icon }) {
  return (
    <div className="border border-dashed border-white/10 rounded-lg p-12 text-center">
      <Icon className="w-10 h-10 text-amber-400 mx-auto mb-4" strokeWidth={1.5} />
      <div className="text-sm text-zinc-400">{label}</div>
    </div>
  );
}
